import {
  ColumnApiModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  CsvExportModule,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
} from 'ag-grid-enterprise';
import type {
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  GridReadyEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import _ from 'lodash';
import { AgGridReact } from 'ag-grid-react';
import {
  AgGridBase,
  DEFAULT_BASE_COL_DEF,
  useAgGridColumnStatePersistence,
  useRefreshServerSideWhen,
  useStableServerSideDatasource,
} from '../AgGridBase';
import { RootState } from 'Rdx/store';
import type { UrlParams } from '../AgGridBase';
import { resolveSsrmSelectedRows } from 'Core-utils/agGridSelection';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getUsersGridColumns } from './getUsersGridColumns';
import type { Employee } from './usersTable';
import {
  mapUrlParamsToUsersGridState,
  mapUsersGridStateToUrlParams,
  usersUrlDataSignature,
  USERS_GRID_COLUMN_STATE_KEY,
  USERS_GRID_CONFIG,
  USERS_GRID_DEFAULT_COLUMN_STATE,
} from './usersGrid.config';
import { useUsersServerSideDatasource } from './useUsersServerSideDatasource';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  RowApiModule,
  TextFilterModule,
  TooltipModule,
  SetFilterModule,
  ColumnApiModule,
  RenderApiModule,
  ValidationModule,
  CsvExportModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
]);

export type UsersAgGridFilterEnums = {
  department: string[];
  organization: string[];
  title: string[];
};

type UsersAgGridSectionProps = {
  filterEnums: UsersAgGridFilterEnums;
  urlFilter: UrlParams;
  isUrlLoading: boolean;
  searchParams: URLSearchParams;
  setSearchParamsState: (filters: Record<string, string | string[] | undefined>) => void;
  onSelectedEmployeesChange: (rows: Employee[]) => void;
  clearSelectionToken: number;
};

const UsersAgGridSection: React.FC<UsersAgGridSectionProps> = ({
  filterEnums,
  urlFilter,
  isUrlLoading,
  setSearchParamsState,
  onSelectedEmployeesChange,
  clearSelectionToken,
}) => {
  const gridRef = useRef<AgGridReact<Employee> | null>(null);
  const organization = useSelector((state: RootState) => state.orgModel.org);
  const lastServerSideStateRef = useRef<SelectionChangedEvent<Employee>['serverSideState']>(null);
  const columnDefs = useMemo(
    () =>
      getUsersGridColumns({
        departmentValues: filterEnums.department,
        organizationValues: filterEnums.organization,
        titleValues: filterEnums.title,
      }),
    [filterEnums.department, filterEnums.organization, filterEnums.title]
  );

  const datasourceUrlFilter = useMemo(
    () => ({
      q: urlFilter?.q,
    }),
    [urlFilter?.q]
  );
  const datasource = useUsersServerSideDatasource(datasourceUrlFilter, organization);
  const stableDatasource = useStableServerSideDatasource(datasource);

  useRefreshServerSideWhen(gridRef, organization);

  const { applyOnGridReady, onColumnEvent } = useAgGridColumnStatePersistence({
    storageKey: USERS_GRID_COLUMN_STATE_KEY,
    defaultState: [...USERS_GRID_DEFAULT_COLUMN_STATE],
  });

  const usersUrlSync = useMemo(
    () => ({
      enabled: true,
      adapter: {
        fromUrl: mapUrlParamsToUsersGridState,
        toUrl: mapUsersGridStateToUrlParams,
      },
      preserveKeys: [],
      debounceMs: 200,
      replaceHistory: true,
      getUrlState: () => ({ ...urlFilter }),
      setUrlState: (next: Record<string, string | string[] | undefined>) => setSearchParamsState(next),
      isUrlLoading,
      urlStateRevision: usersUrlDataSignature(urlFilter),
    }),
    [isUrlLoading, setSearchParamsState, urlFilter]
  );

  const onGridReady = useCallback(
    (event: GridReadyEvent<Employee>) => {
      applyOnGridReady(event.api as unknown as Parameters<typeof applyOnGridReady>[0]);
    },
    [applyOnGridReady]
  );

  const handleColumnEvent = useCallback(
    (
      event:
        | ColumnResizedEvent<Employee>
        | ColumnMovedEvent<Employee>
        | ColumnVisibleEvent<Employee>
        | ColumnPinnedEvent<Employee>
    ) => {
      onColumnEvent(event as unknown as Parameters<typeof onColumnEvent>[0]);
    },
    [onColumnEvent]
  );

  const onSelectionChanged = useCallback(
    (params: SelectionChangedEvent<Employee>) => {
      lastServerSideStateRef.current = params.serverSideState;
      const data = resolveSsrmSelectedRows(params.api, params.serverSideState);
      onSelectedEmployeesChange(data);
    },
    [onSelectedEmployeesChange]
  );

  const handleStoreUpdated = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    const serverSideState = lastServerSideStateRef.current;
    if (!serverSideState || !('selectAll' in serverSideState) || !serverSideState.selectAll) {
      return;
    }
    const data = resolveSsrmSelectedRows<Employee>(api, serverSideState);
    onSelectedEmployeesChange(data);
  }, [onSelectedEmployeesChange]);

  useEffect(() => {
    gridRef.current?.api?.deselectAll();
  }, [clearSelectionToken]);

  const handleSearchChange = useCallback((searchValue: string) => {
    const currentInUrl = (urlFilter?.q as string | undefined) ?? '';
    if (searchValue !== currentInUrl) {
      setSearchParamsState({
          ...urlFilter,
          q: searchValue,
      });
    }
  }, [setSearchParamsState, urlFilter]);

  return (
    <AgGridBase.Root fullWidth>
      <AgGridBase.Table<Employee>
        ref={gridRef}
        columnDefs={columnDefs}
        dataMode="server"
        serverSideDatasource={stableDatasource}
        bodyHeight={USERS_GRID_CONFIG.defaultHeight}
        bodyMinHeight={USERS_GRID_CONFIG.minHeight}
        showSidebar={false}
        showSearchBar={true}
        searchDefaultValue={urlFilter?.q as string | undefined}
        onSearchChange={handleSearchChange}
        movableColumns={false}
        urlSync={usersUrlSync}
        defaultColDef={DEFAULT_BASE_COL_DEF}
        agGridProps={{
          getRowId: (params) => String(params.data?.id ?? ''),
          domLayout: 'normal',
          suppressAggFuncInHeader: true,
          cacheBlockSize: USERS_GRID_CONFIG.cacheBlockSize,
          maxBlocksInCache: 4,
          serverSideInitialRowCount: USERS_GRID_CONFIG.cacheBlockSize,
          rowSelection: { mode: 'multiRow' },
          suppressContextMenu: true,
          onSelectionChanged,
          onModelUpdated: handleStoreUpdated,
          onGridReady,
          onColumnResized: handleColumnEvent,
          onColumnMoved: handleColumnEvent,
          onColumnVisible: handleColumnEvent,
          onColumnPinned: handleColumnEvent,
        }}
      />
    </AgGridBase.Root>
  );
};

export default UsersAgGridSection;
